import { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { MobileExploreShell } from '../components/explore/MobileExploreShell';
import { DesktopExploreShell } from '../components/explore/DesktopExploreShell';
import { formatNumber } from '../utils/format';
import { computeExploreCapacity } from '../utils/exploreCapacity';
import { api } from '../api/client';
import type { Province } from '../types/province';
import type { ExploreRequest, ExploreResult } from '../types/explore';

/**
 * SF-139: named, fully-typed props shared by MobileExploreShell/DesktopExploreShell — same
 * BuildingsShellProps precedent as SF-132. State stays hoisted here (not per-shell) so a
 * breakpoint flip mid-edit doesn't lose the in-progress `acresToExplore` draft.
 */
export interface ExploreShellProps {
  province: Province;
  acresToExplore: number;
  isSubmitting: boolean;
  submitError: string | null;
  successMessage: string | null;
  warningMessage: string | null;
  handleInputChange: (value: number) => void;
  handleSubmit: () => void;
  maxExplorable: number;
  maxBySoldiers: number;
  maxByGold: number;
  maxByPool: number;
  kingdomExplorePool: number;
  isAtWar: boolean;
  soldierCostPerAcre: number;
  goldCostPerAcre: number;
  availableSoldiers: number;
  availableGold: number;
  currentlyExploring: number;
  totalSoldierCost: number;
  totalGoldCost: number;
}

export function Explore() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, refresh } = useProvince();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const [acresToExplore, setAcresToExplore] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const explorationCosts = province?.territory.explorationCosts;
  const soldierCostPerAcre = explorationCosts?.soldierCostPerAcre || 5;
  const goldCostPerAcre = explorationCosts?.goldCostPerAcre || 750;

  const kingdomData = province?.kingdom;
  const kingdomExplorePool = kingdomData?.explorePool || 0;
  const isAtWar = kingdomData?.isAtWar || false;

  const availableSoldiers = province?.population.soldiers || 0;
  const availableGold = province?.resources.gold || 0;

  const currentlyExploring = province?.territory.exploration.reduce(
    (sum, exp) => sum + exp.acres, 0
  ) || 0;

  const { maxBySoldiers, maxByGold, maxByPool, maxExplorable } = useMemo(
    () => computeExploreCapacity(province), [province]
  );

  const totalSoldierCost = acresToExplore * soldierCostPerAcre;
  const totalGoldCost = acresToExplore * goldCostPerAcre;

  // SF-139 (mirrors SF-132's Buildings.tsx reportError/reportSuccess split): mobile routes
  // feedback through the app-wide toast system; desktop keeps its existing inline messages.
  const reportError = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'failure');
    } else {
      setSubmitError(message);
    }
  };

  const reportSuccess = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'success');
    } else {
      setSuccessMessage(message);
    }
  };

  const handleInputChange = (value: number) => {
    setAcresToExplore(Math.min(Math.max(0, value), maxExplorable));
    setSubmitError(null);
    setSuccessMessage(null);
    setWarningMessage(null);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSuccessMessage(null);
    setWarningMessage(null);

    if (acresToExplore < 1) {
      reportError('Must explore at least 1 acre');
      return;
    }

    if (acresToExplore > maxExplorable) {
      reportError(`Cannot explore more than ${maxExplorable} acres`);
      return;
    }

    setIsSubmitting(true);

    try {
      const request: ExploreRequest = { acresToExplore };
      const response = await api.post<ExploreResult>(
        `/api/worlds/${worldId}/provinces/${provinceId}/explore`,
        request
      );

      if (response.error) {
        reportError(response.error);
      } else if (response.data) {
        const result = response.data;

        if (result.success) {
          const message = `Exploration started: ${result.acresExplored} acres for ${formatNumber(result.soldiersCost)} soldiers and ${formatNumber(result.goldCost)} gold. Land arrives in ${result.ticksUntilArrival} days.`;

          reportSuccess(platform === 'mobile' && result.warning ? `${message} Note: ${result.warning}` : message);
          if (platform !== 'mobile' && result.warning) {
            setWarningMessage(result.warning);
          }

          setAcresToExplore(0);
          await refresh();
        } else if (result.failureReason) {
          reportError(result.failureReason);
        }
      }
    } catch {
      reportError('Failed to submit exploration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message={error || 'Failed to load province'} />
        </div>
      </div>
    );
  }

  const shellProps: ExploreShellProps = {
    province,
    acresToExplore,
    isSubmitting,
    submitError,
    successMessage,
    warningMessage,
    handleInputChange,
    handleSubmit,
    maxExplorable,
    maxBySoldiers,
    maxByGold,
    maxByPool,
    kingdomExplorePool,
    isAtWar,
    soldierCostPerAcre,
    goldCostPerAcre,
    availableSoldiers,
    availableGold,
    currentlyExploring,
    totalSoldierCost,
    totalGoldCost,
  };

  return platform === 'mobile'
    ? <MobileExploreShell {...shellProps} />
    : <DesktopExploreShell {...shellProps} />;
}
